import { useEffect, useState } from "react";
import { gitlabApiPrefix } from "../components/SupabaseClient";
import { fetchAllGitLabPages } from "../utils/gitlabPagination";

/**
 * Fetches one directory level from a GitLab repository.
 *
 * @param {number|string|null} projectId
 * @param {string} path
 * @returns {{
 *   items: object[],
 *   commitInfo: Record<string, { committedDate?: string, message?: string }>,
 *   loading: boolean,
 *   error: string | null,
 * }}
 */
export const useGitLabRepoTree = (projectId, path = "") => {
  const [items, setItems] = useState([]);
  const [commitInfo, setCommitInfo] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!projectId) {
      setItems([]);
      setCommitInfo({});
      setError(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    const { signal } = controller;

    const loadDirectory = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({ per_page: "100" });
        if (path) params.set("path", path);

        const treeItems = await fetchAllGitLabPages(
          `${gitlabApiPrefix}/api/v4/projects/${projectId}/repository/tree?${params}`,
          { signal },
        );
        if (signal.aborted) return;

        setItems(treeItems);
        setLoading(false);

        const commitEntries = await Promise.all(
          treeItems.map(async (item) => {
            try {
              const commitParams = new URLSearchParams({
                path: item.path,
                per_page: "1",
              });
              const commitResp = await fetch(
                `${gitlabApiPrefix}/api/v4/projects/${projectId}/repository/commits?${commitParams}`,
                { signal },
              );

              if (!commitResp.ok || signal.aborted) return null;

              const commits = await commitResp.json();
              const latestCommit = commits[0];

              if (!latestCommit) return null;

              return [
                item.path,
                {
                  committedDate: latestCommit.committed_date,
                  message:
                    latestCommit.title ||
                    latestCommit.message?.split("\n")[0] ||
                    "",
                },
              ];
            } catch (commitError) {
              if (commitError.name !== "AbortError") {
                // Commit metadata is best-effort; leave the row partially populated.
              }
              return null;
            }
          }),
        );

        if (signal.aborted) return;

        setCommitInfo(Object.fromEntries(commitEntries.filter(Boolean)));
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error(
          `[useGitLabRepoTree] Failed to fetch tree at "${path}":`,
          err,
        );
        setItems([]);
        setCommitInfo({});
        setError(err.message);
      } finally {
        if (!signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadDirectory();

    return () => controller.abort();
  }, [path, projectId]);

  return { items, commitInfo, loading, error };
};
